import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  QrCode,
  ChevronLeft,
  Search,
  MapPin,
  Edit2,
  ShoppingCart,
  Check,
  X,
  Lock,
  Unlock,
  AlertTriangle,
  Camera,
  Loader2,
  Minus,
  Plus,
  PlusCircle,
  ExternalLink,
  Box,
  Trash2
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  received: "bg-blue-100 text-blue-800",
  available: "bg-green-100 text-green-800",
  hold: "bg-purple-100 text-purple-800",
  low_stock: "bg-red-100 text-red-800"
};

const STATUS_LABELS = {
  pending: "Pending",
  received: "Received",
  available: "Available",
  hold: "On Hold",
  low_stock: "Low Stock"
};

export const MobileInventory = ({ user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState({ locations: [], sub_locations: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [containerFilter, setContainerFilter] = useState("all");
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const html5QrCodeRef = useRef(null);
  
  // Item detail sheet
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: "", location: "", sub_location: "", quantity: 0, location_quantities: [] });
  
  // Reorder dialog
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderQty, setReorderQty] = useState(1);
  
  // Use from stock state
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [useQty, setUseQty] = useState(1);
  const [useLoading, setUseLoading] = useState(false);
  const [useFromLocation, setUseFromLocation] = useState("");
  const [useFromSubLocation, setUseFromSubLocation] = useState("");
  
  // Hold quantity state
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdQty, setHoldQty] = useState(1);
  const [holdReason, setHoldReason] = useState("");
  const [holdLoading, setHoldLoading] = useState(false);
  
  // Add new item state
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    quantity: 1,
    location: "",
    sub_location: "",
    minimum_stock: 0,
    notes: "",
    purchase_link: "",
    container_type: "none",
    container_size: "none"
  });
  const [addLoading, setAddLoading] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Add to location state
  const [addToLocationOpen, setAddToLocationOpen] = useState(false);
  const [addToLocationData, setAddToLocationData] = useState({
    location: "",
    sub_location: "",
    quantity: 1
  });
  const [addToLocationLoading, setAddToLocationLoading] = useState(false);
  
  // Hold location state  
  const [holdLocation, setHoldLocation] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/locations`)
      ]);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  // QR Scanner
  const startScanner = async () => {
    setScannerOpen(true);
    setScanning(false);
    
    setTimeout(async () => {
      try {
        html5QrCodeRef.current = new Html5Qrcode("mobile-inventory-scanner", {
          verbose: false
        });
        
        const containerWidth = document.getElementById("mobile-inventory-scanner")?.offsetWidth || 300;
        const qrboxWidth = Math.min(containerWidth - 40, 280);
        
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: qrboxWidth, height: qrboxWidth * 0.4 },
            aspectRatio: 1.0,
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            }
          },
          async (decodedText) => {
            // Look up the item by QR code
            console.log("Scanned QR:", decodedText);
            await lookupItem(decodedText);
          },
          (errorMessage) => {
            // Ignore scan errors
          }
        );
        setScanning(true);
      } catch (err) {
        console.error("Scanner error:", err);
        toast.error("Could not access camera");
        setScannerOpen(false);
      }
    }, 300);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
    setScannerOpen(false);
  };

  const lookupItem = async (qrCode) => {
    try {
      stopScanner();
      const response = await axios.get(`${API}/inventory/${qrCode}`);
      setSelectedItem(response.data);
      setEditData({
        name: response.data.name || "",
        location: response.data.location || "",
        sub_location: response.data.sub_location || "",
        quantity: response.data.quantity || 0,
        location_quantities: response.data.location_quantities || []
      });
      setDetailOpen(true);
    } catch (error) {
      toast.error("Item not found");
      // Keep scanner open to try again
      setScannerOpen(true);
      startScanner();
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    
    try {
      // If no location_quantities, create one from the simple location/quantity fields
      let locationQuantities = editData.location_quantities || [];
      let totalQuantity = editData.quantity;
      
      if (locationQuantities.length === 0 && editData.location) {
        // Create a single location_quantities entry from the simple form
        locationQuantities = [{
          location: editData.location,
          sub_location: editData.sub_location || "",
          quantity: editData.quantity
        }];
        totalQuantity = editData.quantity;
      } else if (locationQuantities.length > 0) {
        // Calculate total from location_quantities
        totalQuantity = locationQuantities.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0);
      }
      
      const payload = {
        ...editData,
        quantity: totalQuantity,
        location_quantities: locationQuantities
      };
      
      await axios.put(`${API}/inventory/${selectedItem.id}`, payload);
      toast.success("Item updated!");
      setEditMode(false);
      // Refresh item data
      const response = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(response.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleAddToReorder = async () => {
    if (!selectedItem) return;
    
    try {
      await axios.post(`${API}/reorder-list`, {
        inventory_item_id: selectedItem.id,
        quantity_needed: reorderQty,
        notes: ""
      });
      toast.success("Added to reorder list!");
      setReorderDialogOpen(false);
      setReorderQty(1);
    } catch (error) {
      toast.error("Failed to add to reorder list");
    }
  };

  const handleHold = async () => {
    if (!selectedItem) return;
    
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/hold`, { reason: "Mobile hold" });
      toast.success("Item placed on hold");
      const response = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to hold item");
    }
  };

  const handleRelease = async () => {
    if (!selectedItem) return;
    
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/release`);
      toast.success("Item released from hold");
      const response = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to release item");
    }
  };

  // Use from stock handler
  const handleUseFromStock = async () => {
    if (!selectedItem) return;
    setUseLoading(true);
    
    try {
      let response;
      // If item has locations and user selected one, use from specific location
      if (selectedItem.location_quantities?.length > 0 && useFromLocation) {
        response = await axios.post(`${API}/inventory/${selectedItem.id}/use-from-location`, {
          location: useFromLocation,
          quantity: useQty
        });
      } else {
        // Use the general endpoint (will deduct from first available location)
        response = await axios.post(`${API}/inventory/${selectedItem.id}/use`, {
          quantity: useQty
        });
      }
      toast.success(`Used ${useQty} from stock`);
      setUseDialogOpen(false);
      setUseQty(1);
      setUseFromLocation("");
      setUseFromSubLocation("");
      // Refresh item data
      const itemResponse = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(itemResponse.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to use from stock");
    } finally {
      setUseLoading(false);
    }
  };

  // Delete item handler
  const handleDeleteItem = async (e) => {
    e?.preventDefault();
    if (!selectedItem) return;
    setDeleteLoading(true);
    
    try {
      await axios.delete(`${API}/inventory/${selectedItem.id}`);
      toast.success("Item deleted");
      setDeleteDialogOpen(false);
      setDetailOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete item");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Quick use (minus 1)
  const handleQuickUse = async (item, e) => {
    e.stopPropagation();
    if (item.quantity <= 0) {
      toast.error("No stock available");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory/${item.id}/use`, { quantity: 1 });
      toast.success(`Used 1 ${item.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to use item");
    }
  };

  // Hold quantity handler
  const handleHoldQuantity = async () => {
    if (!selectedItem) return;
    setHoldLoading(true);
    
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/hold-quantity`, {
        quantity: holdQty,
        reason: holdReason
      });
      toast.success(`Put ${holdQty} on hold`);
      setHoldDialogOpen(false);
      setHoldQty(1);
      setHoldReason("");
      const response = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place hold");
    } finally {
      setHoldLoading(false);
    }
  };

  // Add new item handler
  const handleAddNewItem = async () => {
    if (!newItem.name.trim()) {
      toast.error("Item name is required");
      return;
    }
    setAddLoading(true);
    
    try {
      await axios.post(`${API}/inventory`, {
        name: newItem.name,
        description: "",
        location: newItem.location,
        sub_location: newItem.sub_location,
        quantity: newItem.quantity,
        minimum_stock: newItem.minimum_stock,
        cost_per_piece: 0,
        library_use_only: false,
        notes: newItem.notes,
        purchase_link: newItem.purchase_link,
        container_type: newItem.container_type,
        container_size: newItem.container_size
      });
      toast.success("Item added successfully!");
      setAddItemOpen(false);
      setNewItem({ name: "", quantity: 1, location: "", sub_location: "", minimum_stock: 0, notes: "", purchase_link: "", container_type: "none", container_size: "none" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    } finally {
      setAddLoading(false);
    }
  };

  // Add to specific location handler
  const handleAddToLocation = async () => {
    if (!selectedItem || !addToLocationData.location) {
      toast.error("Please select a location");
      return;
    }
    setAddToLocationLoading(true);
    
    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/add-to-location`, {
        location: addToLocationData.location,
        sub_location: addToLocationData.sub_location,
        quantity: addToLocationData.quantity
      });
      toast.success(`Added ${addToLocationData.quantity} to ${addToLocationData.location}`);
      setAddToLocationOpen(false);
      setAddToLocationData({ location: "", sub_location: "", quantity: 1 });
      const response = await axios.get(`${API}/inventory/${selectedItem.id}`);
      setSelectedItem(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to location");
    } finally {
      setAddToLocationLoading(false);
    }
  };

  const getSubLocations = (location) => {
    return locations.sub_locations[location] || [];
  };

  // Filter items by search and container type
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.qr_code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesContainer = containerFilter === "all" || 
      (containerFilter === "box" && item.container_type === "box") ||
      (containerFilter === "bag" && item.container_type === "bag") ||
      (containerFilter === "none" && (!item.container_type || item.container_type === "none"));
    return matchesSearch && matchesContainer;
  }).slice(0, 20);

  return (
    <div className="px-4 py-4" data-testid="mobile-inventory">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/mobile")}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold">Inventory</h1>
            <p className="text-xs text-muted-foreground">Scan or search items</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAddItemOpen(true)}
            size="sm"
            variant="outline"
            data-testid="mobile-add-inventory-btn"
          >
            <PlusCircle className="h-4 w-4" />
          </Button>
          <Button
            onClick={startScanner}
            size="sm"
            className="bg-gradient-to-r from-indigo-500 to-purple-600 shadow-md"
            data-testid="mobile-scan-inventory-btn"
          >
            <QrCode className="h-4 w-4 mr-1" /> Scan
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search items or QR codes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="mobile-inventory-search"
        />
      </div>

      {/* Container Filter */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <Button
          variant={containerFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setContainerFilter("all")}
          className="flex-shrink-0"
        >
          All
        </Button>
        <Button
          variant={containerFilter === "box" ? "default" : "outline"}
          size="sm"
          onClick={() => setContainerFilter("box")}
          className="flex-shrink-0"
        >
          <Box className="h-3 w-3 mr-1" /> Boxes
        </Button>
        <Button
          variant={containerFilter === "bag" ? "default" : "outline"}
          size="sm"
          onClick={() => setContainerFilter("bag")}
          className="flex-shrink-0"
        >
          <Package className="h-3 w-3 mr-1" /> Bags
        </Button>
        <Button
          variant={containerFilter === "none" ? "default" : "outline"}
          size="sm"
          onClick={() => setContainerFilter("none")}
          className="flex-shrink-0"
        >
          No Container
        </Button>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-3" strokeWidth={1} />
            <p className="font-medium">No items found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Scan a QR code or search for items
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item) => (
            <Card
              key={item.id}
              className="overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => {
                setSelectedItem(item);
                setEditData({
                  name: item.name || "",
                  location: item.location || "",
                  sub_location: item.sub_location || "",
                  quantity: item.quantity || 0,
                  location_quantities: item.location_quantities || []
                });
                setDetailOpen(true);
              }}
              data-testid={`mobile-inventory-item-${item.id}`}
            >
              <div className={`h-1 ${
                item.status === "available" ? "bg-green-500" :
                item.status === "hold" ? "bg-purple-500" :
                item.status === "low_stock" ? "bg-red-500" :
                item.status === "pending" ? "bg-yellow-500" : "bg-gray-300"
              }`} />
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">
                        {item.qr_code}
                      </code>
                      {item.library_use_only && (
                        <Lock className="h-3 w-3 text-amber-600" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {item.location || "No location"}
                      {item.sub_location && ` - ${item.sub_location}`}
                      {item.container_type && item.container_type !== "none" && (
                        <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                          {item.container_size?.toUpperCase()} {item.container_type === "box" ? "Box" : "Bag"}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quick Use Button */}
                    {item.status !== "hold" && item.quantity > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 w-10 p-0 rounded-full border-red-200 hover:bg-red-50"
                        onClick={(e) => handleQuickUse(item, e)}
                        data-testid={`quick-use-${item.id}`}
                      >
                        <Minus className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold">{item.quantity}</p>
                      <Badge className={`text-xs ${STATUS_COLORS[item.status]}`}>
                        {STATUS_LABELS[item.status]}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* QR Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => !open && stopScanner()}>
        <DialogContent className="max-w-[95vw] rounded-2xl p-0 gap-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-heading flex items-center gap-2">
              <QrCode className="h-5 w-5 text-indigo-600" />
              Scan Inventory QR
            </DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <div
              id="mobile-inventory-scanner"
              className="w-full rounded-lg overflow-hidden bg-black"
              style={{ minHeight: "300px" }}
            />
            <p className="text-center text-sm text-muted-foreground mt-3">
              {scanning ? "Point camera at QR code" : "Starting camera..."}
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={stopScanner}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  const manual = prompt("Enter QR code or Item ID:");
                  if (manual) {
                    lookupItem(manual.trim());
                  }
                }}
              >
                Enter Manually
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={(open) => {
        setDetailOpen(open);
        if (!open) {
          setEditMode(false);
          setSelectedItem(null);
        }
      }}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              {selectedItem?.name}
            </SheetTitle>
          </SheetHeader>
          
          {selectedItem && (
            <div className="py-4 space-y-4 overflow-y-auto max-h-[calc(85vh-120px)]">
              {/* Status & QR Code */}
              <div className="flex items-center justify-between">
                <Badge className={`${STATUS_COLORS[selectedItem.status]} text-sm px-3 py-1`}>
                  {STATUS_LABELS[selectedItem.status]}
                </Badge>
                <code className="text-sm bg-stone-100 px-3 py-1 rounded font-mono">
                  {selectedItem.qr_code}
                </code>
              </div>

              {/* Hold info */}
              {selectedItem.status === "hold" && selectedItem.hold_by_name && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>On hold by:</strong> {selectedItem.hold_by_name}
                  </p>
                  {selectedItem.hold_reason && (
                    <p className="text-sm text-purple-600 mt-1">{selectedItem.hold_reason}</p>
                  )}
                </div>
              )}

              {/* Low stock warning */}
              {selectedItem.status === "low_stock" && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-800">Low stock - consider reordering</p>
                </div>
              )}

              {/* Library only badge */}
              {selectedItem.library_use_only && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <Lock className="h-4 w-4 text-amber-600" />
                  <p className="text-sm text-amber-800">Library Use Only - Cannot be dedicated to programs</p>
                </div>
              )}

              {/* Editable Fields */}
              {editMode ? (
                <div className="space-y-4 p-4 bg-stone-50 rounded-lg">
                  <div>
                    <Label>Item Name</Label>
                    <Input
                      className="mt-1"
                      placeholder="Item name"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      data-testid="edit-item-name"
                    />
                  </div>
                  
                  {/* Multi-Location Editing */}
                  {editData.location_quantities?.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-medium">Stock by Location</Label>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          Total: {editData.location_quantities.reduce((sum, lq) => sum + (parseFloat(lq.quantity) || 0), 0)}
                        </Badge>
                      </div>
                      
                      {editData.location_quantities.map((lq, idx) => (
                        <div key={idx} className="p-3 bg-stone-50 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-muted-foreground">Location {idx + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => {
                                const newLqs = editData.location_quantities.filter((_, i) => i !== idx);
                                setEditData({ ...editData, location_quantities: newLqs });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <Select
                            value={lq.location}
                            onValueChange={(value) => {
                              const newLqs = [...editData.location_quantities];
                              newLqs[idx] = { ...lq, location: value, sub_location: "" };
                              setEditData({ ...editData, location_quantities: newLqs });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              {locations.locations.map(loc => (
                                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Sub-location"
                              value={lq.sub_location || ""}
                              onChange={(e) => {
                                const newLqs = [...editData.location_quantities];
                                newLqs[idx] = { ...lq, sub_location: e.target.value };
                                setEditData({ ...editData, location_quantities: newLqs });
                              }}
                            />
                            <Input
                              type="number"
                              min="0"
                              step="0.25"
                              placeholder="Qty"
                              value={lq.quantity}
                              onChange={(e) => {
                                const newLqs = [...editData.location_quantities];
                                newLqs[idx] = { ...lq, quantity: parseFloat(e.target.value) || 0 };
                                setEditData({ ...editData, location_quantities: newLqs });
                              }}
                            />
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed"
                        onClick={() => {
                          setEditData({
                            ...editData,
                            location_quantities: [
                              ...editData.location_quantities,
                              { location: "", sub_location: "", quantity: 0 }
                            ]
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Location
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label>Location</Label>
                        <Select
                          value={editData.location}
                          onValueChange={(value) => setEditData({ ...editData, location: value, sub_location: "" })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.locations.map(loc => (
                              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {editData.location === "Staff Kitchen" ? (
                        <div>
                          <Label>Locker #</Label>
                          <Input
                            className="mt-1"
                            placeholder="e.g., 7"
                            value={editData.sub_location}
                            onChange={(e) => setEditData({ ...editData, sub_location: e.target.value })}
                          />
                        </div>
                      ) : getSubLocations(editData.location).length > 0 && (
                        <div>
                          <Label>Sub-Location</Label>
                          <Select
                            value={editData.sub_location}
                            onValueChange={(value) => setEditData({ ...editData, sub_location: value === "none" ? "" : value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select sub-location" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {getSubLocations(editData.location).map(sub => (
                                <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      <div>
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.25"
                          className="mt-1"
                          value={editData.quantity}
                          onChange={(e) => setEditData({ ...editData, quantity: parseFloat(e.target.value) || 0 })}
                          data-testid="edit-item-quantity"
                        />
                      </div>
                      
                      {/* Option to convert to multi-location */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed text-blue-600"
                        onClick={() => {
                          // Convert single location to multi-location
                          const initialLocations = editData.location 
                            ? [{ location: editData.location, sub_location: editData.sub_location || "", quantity: editData.quantity }]
                            : [{ location: "", sub_location: "", quantity: editData.quantity }];
                          setEditData({
                            ...editData,
                            location_quantities: initialLocations
                          });
                        }}
                      >
                        <MapPin className="h-4 w-4 mr-1" /> Manage Multiple Locations
                      </Button>
                    </>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-brand-green hover:bg-brand-green/90"
                      onClick={handleSaveEdit}
                    >
                      <Check className="h-4 w-4 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Location Info - with breakdown for multiple locations */}
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground">Location</p>
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                    </div>
                    {selectedItem.location_quantities?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedItem.location_quantities.map((lq, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <span className="font-medium">
                              {lq.location}
                              {lq.sub_location && <span className="text-muted-foreground"> - {lq.sub_location}</span>}
                            </span>
                            <Badge variant="outline" className="font-mono">{lq.quantity}</Badge>
                          </div>
                        ))}
                        <div className="pt-2 mt-2 border-t flex justify-between items-center">
                          <span className="text-sm font-medium">Total</span>
                          <span className="text-lg font-bold">{selectedItem.quantity}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium">
                        {selectedItem.location || "Not set"}
                        {selectedItem.sub_location && ` - ${selectedItem.sub_location}`}
                      </p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Quantity</p>
                      <p className="text-2xl font-bold">{selectedItem.quantity}</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Min Stock</p>
                      <p className="text-2xl font-bold">{selectedItem.minimum_stock || "-"}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Cost per Piece</p>
                    <p className="font-medium">${selectedItem.cost_per_piece?.toFixed(2) || "0.00"}</p>
                  </div>
                  
                  {selectedItem.purchase_link && (
                    <a
                      href={selectedItem.purchase_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-blue-50 rounded-lg border border-blue-200"
                      data-testid="mobile-purchase-link"
                    >
                      <p className="text-xs text-blue-600">Purchase Link</p>
                      <p className="text-sm font-medium text-blue-700 flex items-center gap-1 truncate">
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        {selectedItem.purchase_link.length > 35 
                          ? selectedItem.purchase_link.substring(0, 35) + "..." 
                          : selectedItem.purchase_link}
                      </p>
                    </a>
                  )}
                  
                  {selectedItem.description && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Description</p>
                      <p className="text-sm">{selectedItem.description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {!editMode && (
                <div className="space-y-2 pt-4 border-t">
                  {/* Add to Location Button */}
                  <Button
                    className="w-full justify-start bg-green-50 hover:bg-green-100 text-green-700 border border-green-200"
                    onClick={() => setAddToLocationOpen(true)}
                    data-testid="add-to-location-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Stock to Location
                  </Button>
                  
                  {/* Use from Stock Button */}
                  {selectedItem.status !== "hold" && selectedItem.quantity > 0 && (
                    <Button
                      className="w-full justify-start bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                      onClick={() => setUseDialogOpen(true)}
                      data-testid="use-from-stock-btn"
                    >
                      <Minus className="h-4 w-4 mr-2" /> Use from Stock
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setEditMode(true)}
                    data-testid="edit-inventory-item-btn"
                  >
                    <Edit2 className="h-4 w-4 mr-2" /> Edit Item Details
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setReorderDialogOpen(true)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" /> Add to Reorder List
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="delete-inventory-item-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete Item
                  </Button>
                  
                  {selectedItem.status !== "hold" && !selectedItem.library_use_only && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setHoldDialogOpen(true)}
                    >
                      <Lock className="h-4 w-4 mr-2" /> Put on Hold
                    </Button>
                  )}
                  
                  {selectedItem.status === "hold" && (selectedItem.hold_by === user?.id || user?.is_admin || user?.role === "supervisor") && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleRelease}
                    >
                      <Unlock className="h-4 w-4 mr-2" /> Release from Hold
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Reorder Dialog */}
      <Dialog open={reorderDialogOpen} onOpenChange={setReorderDialogOpen}>
        <DialogContent className="max-w-[90vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add to Reorder List</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-3">
              How many <strong>{selectedItem?.name}</strong> do you need to reorder?
            </p>
            <div>
              <Label>Quantity Needed</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                value={reorderQty}
                onChange={(e) => setReorderQty(parseFloat(e.target.value) || 0.25)}
                className="mt-1"
              />
            </div>
            {selectedItem?.cost_per_piece > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Estimated cost: ${(reorderQty * selectedItem.cost_per_piece).toFixed(2)}
              </p>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setReorderDialogOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleAddToReorder} className="flex-1">
              <ShoppingCart className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use from Stock Dialog */}
      <Dialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <DialogContent className="max-w-[90vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minus className="h-5 w-5 text-red-600" />
              Use from Stock
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              How many <strong>{selectedItem?.name}</strong> are you using?
            </p>
            
            {/* Location Selection for multi-location items */}
            {selectedItem?.location_quantities?.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Location</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedItem.location_quantities.filter(lq => lq.quantity > 0).map((lq, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setUseFromLocation(lq.location);
                        setUseFromSubLocation(lq.sub_location || "");
                        setUseQty(Math.min(useQty, lq.quantity));
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        useFromLocation === lq.location && useFromSubLocation === (lq.sub_location || "")
                          ? "border-red-500 bg-red-50" 
                          : "border-stone-200 hover:border-red-300 hover:bg-red-50/50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">{lq.location}</span>
                        <Badge variant="outline" className={lq.quantity <= 5 ? "text-amber-600 border-amber-300" : ""}>
                          {lq.quantity} available
                        </Badge>
                      </div>
                      {lq.sub_location && (
                        <span className="text-xs text-muted-foreground">{lq.sub_location}</span>
                      )}
                    </div>
                  ))}
                </div>
                {useFromLocation && (
                  <p className="text-xs text-muted-foreground">
                    Max from {useFromLocation}: {selectedItem.location_quantities.find(lq => lq.location === useFromLocation && (lq.sub_location || "") === useFromSubLocation)?.quantity || 0}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Available: <strong>{selectedItem?.quantity}</strong>
              </p>
            )}
            
            <div>
              <Label>Quantity to Use</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                max={
                  selectedItem?.location_quantities?.length > 0 && useFromLocation
                    ? selectedItem.location_quantities.find(lq => lq.location === useFromLocation && (lq.sub_location || "") === useFromSubLocation)?.quantity || 0.25
                    : selectedItem?.quantity || 0.25
                }
                value={useQty}
                onChange={(e) => {
                  const max = selectedItem?.location_quantities?.length > 0 && useFromLocation
                    ? selectedItem.location_quantities.find(lq => lq.location === useFromLocation && (lq.sub_location || "") === useFromSubLocation)?.quantity || 0.25
                    : selectedItem?.quantity || 0.25;
                  setUseQty(Math.min(parseFloat(e.target.value) || 0.25, max));
                }}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setUseDialogOpen(false)} className="flex-1" disabled={useLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUseFromStock} 
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={useLoading || (selectedItem?.location_quantities?.length > 0 && !useFromLocation)}
            >
              {useLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Minus className="h-4 w-4 mr-1" />}
              Use {useQty}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold Dialog */}
      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent className="max-w-[90vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-purple-600" />
              Put on Hold
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Put <strong>{selectedItem?.name}</strong> on hold. Holds automatically expire after 5 days.
            </p>
            <div>
              <Label>Quantity to Hold</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                max={selectedItem?.quantity || 0.25}
                value={holdQty}
                onChange={(e) => setHoldQty(Math.min(parseFloat(e.target.value) || 0.25, selectedItem?.quantity || 0.25))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="e.g., For upcoming program"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setHoldDialogOpen(false)} className="flex-1" disabled={holdLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleHoldQuantity} 
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={holdLoading}
            >
              {holdLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
              Hold {holdQty}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="max-w-[90vw] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-green-600" />
              Add New Item
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="e.g., Craft Scissors"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Min Stock</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  value={newItem.minimum_stock}
                  onChange={(e) => setNewItem({ ...newItem, minimum_stock: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Select
                value={newItem.location}
                onValueChange={(value) => setNewItem({ ...newItem, location: value, sub_location: "" })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newItem.location && getSubLocations(newItem.location).length > 0 && (
              <div>
                <Label>Sub-Location</Label>
                <Select
                  value={newItem.sub_location}
                  onValueChange={(value) => setNewItem({ ...newItem, sub_location: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select sub-location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {getSubLocations(newItem.location).map(sub => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newItem.notes}
                onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                placeholder="Optional notes..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Purchase Link (optional)</Label>
              <Input
                value={newItem.purchase_link}
                onChange={(e) => setNewItem({ ...newItem, purchase_link: e.target.value })}
                placeholder="https://amazon.com/dp/..."
                className="mt-1"
              />
            </div>
            {/* Container Type & Size */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Container</Label>
                <Select
                  value={newItem.container_type}
                  onValueChange={(value) => setNewItem({ 
                    ...newItem, 
                    container_type: value,
                    container_size: value === "none" ? "none" : (newItem.container_size === "none" ? "m" : newItem.container_size)
                  })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="bag">Bag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newItem.container_type !== "none" && (
                <div>
                  <Label>Size</Label>
                  <Select
                    value={newItem.container_size}
                    onValueChange={(value) => setNewItem({ ...newItem, container_size: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">SM</SelectItem>
                      <SelectItem value="m">M</SelectItem>
                      <SelectItem value="l">L</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setAddItemOpen(false)} className="flex-1" disabled={addLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddNewItem} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={addLoading || !newItem.name.trim()}
            >
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4 mr-1" />}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Location Dialog */}
      <Dialog open={addToLocationOpen} onOpenChange={setAddToLocationOpen}>
        <DialogContent className="max-w-[90vw] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Add Stock to Location
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Add <strong>{selectedItem?.name}</strong> to a specific location
            </p>
            
            {/* Current locations */}
            {selectedItem?.location_quantities?.length > 0 && (
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Current Stock:</p>
                {selectedItem.location_quantities.map((lq, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span>
                      {lq.location}
                      {lq.sub_location && <span className="text-muted-foreground"> - {lq.sub_location}</span>}
                    </span>
                    <span className="font-medium">{lq.quantity}</span>
                  </div>
                ))}
              </div>
            )}
            
            <div>
              <Label>Location *</Label>
              <Select
                value={addToLocationData.location}
                onValueChange={(value) => setAddToLocationData({ ...addToLocationData, location: value, sub_location: "" })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {addToLocationData.location === "Staff Kitchen" ? (
              <div>
                <Label>Locker # (optional)</Label>
                <Input
                  className="mt-1"
                  placeholder="e.g., 7"
                  value={addToLocationData.sub_location}
                  onChange={(e) => setAddToLocationData({ ...addToLocationData, sub_location: e.target.value })}
                />
              </div>
            ) : getSubLocations(addToLocationData.location).length > 0 && (
              <div>
                <Label>Sub-Location (optional)</Label>
                <Select
                  value={addToLocationData.sub_location}
                  onValueChange={(value) => setAddToLocationData({ ...addToLocationData, sub_location: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select sub-location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {getSubLocations(addToLocationData.location).map(sub => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Quantity to Add</Label>
              <Input
                type="number"
                min="0.25"
                step="0.25"
                value={addToLocationData.quantity}
                onChange={(e) => setAddToLocationData({ ...addToLocationData, quantity: parseFloat(e.target.value) || 0.25 })}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setAddToLocationOpen(false)} className="flex-1" disabled={addToLocationLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddToLocation} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={addToLocationLoading || !addToLocationData.location}
            >
              {addToLocationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add {addToLocationData.quantity}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-[90vw] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Item
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedItem?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading} data-testid="mobile-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="mobile-delete-confirm-btn"
            >
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
